import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import {
  listUsers, setUserRole, removeUser, forceLogoutUser, toggleUserNotifications,
  setAccountEnabled, setUserLimits,
  setTestAlertsEnabled,
  getAdminEvents, deleteAdminEvents,
  listAllowedEmails, addAllowedEmail, removeAllowedEmail, updateAllowedEmailNote,
  getAdminSessionSummary,
} from '../api/admin'
import type { AdminEventType } from '../types'

export const USERS_KEY = ['admin', 'users'] as const
export const ALLOWLIST_KEY = ['admin', 'allowlist'] as const

// ── Users ──────────────────────────────────────────────────────────────────

export function useAdminUsers(enabled = true) {
  return useQuery({ queryKey: USERS_KEY, queryFn: listUsers, enabled, placeholderData: keepPreviousData })
}

export function useSetUserRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      setUserRole(userId, role),
    onSuccess: () => qc.invalidateQueries({ queryKey: USERS_KEY }),
  })
}

export function useRemoveUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => removeUser(userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: USERS_KEY }),
  })
}

export function useForceLogoutUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => forceLogoutUser(userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: USERS_KEY }),
  })
}

export function useToggleUserNotifications() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, enabled }: { userId: string; enabled: boolean }) =>
      toggleUserNotifications(userId, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: USERS_KEY }),
  })
}

export function useSetAccountEnabled() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, enabled }: { userId: string; enabled: boolean }) =>
      setAccountEnabled(userId, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: USERS_KEY }),
  })
}

export function useSetUserLimits() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      userId,
      maxPhoneNumbers,
      maxNotificationChannels,
    }: {
      userId: string
      maxPhoneNumbers: number
      maxNotificationChannels: number
    }) => setUserLimits(userId, maxPhoneNumbers, maxNotificationChannels),
    onSuccess: () => qc.invalidateQueries({ queryKey: USERS_KEY }),
  })
}

// ── Events ─────────────────────────────────────────────────────────────────

export function useAdminEvents(
  type: AdminEventType | null,
  from: string | null,
  to: string | null,
  cursor: string | null,
  pageSize: number,
) {
  return useQuery({
    queryKey: ['admin', 'events', type, from, to, cursor, pageSize],
    queryFn: () => getAdminEvents(type, from, to, cursor, pageSize),
  })
}

export function useDeleteAdminEvents() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (eventIds: string[]) => deleteAdminEvents(eventIds),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'events'] }),
  })
}

// ── Email allowlist ────────────────────────────────────────────────────────

export function useAllowedEmails() {
  return useQuery({ queryKey: ALLOWLIST_KEY, queryFn: listAllowedEmails, placeholderData: keepPreviousData })
}

export function useAddAllowedEmail() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ email, note }: { email: string; note?: string }) =>
      addAllowedEmail(email, note),
    onSuccess: () => qc.invalidateQueries({ queryKey: ALLOWLIST_KEY }),
  })
}

export function useRemoveAllowedEmail() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (email: string) => removeAllowedEmail(email),
    onSuccess: () => qc.invalidateQueries({ queryKey: ALLOWLIST_KEY }),
  })
}

export function useUpdateAllowedEmailNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ email, note }: { email: string; note: string }) =>
      updateAllowedEmailNote(email, note),
    onSuccess: () => qc.invalidateQueries({ queryKey: ALLOWLIST_KEY }),
  })
}

export function useSetTestAlertsEnabled() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, enabled }: { userId: string; enabled: boolean }) =>
      setTestAlertsEnabled(userId, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: USERS_KEY }),
  })
}

// ── Admin sessions ─────────────────────────────────────────────────────────

export function useAdminSessionSummary(userId: string, month: string) {
  return useQuery({
    queryKey: ['admin', 'session-summary', userId, month],
    queryFn: () => getAdminSessionSummary(userId, month),
    enabled: !!userId && !!month,
    staleTime: 0,
  })
}
